import { DragEvent } from "react";
import { useDragContext } from "./DragContext";
import { CalendarSchedule } from "./types";

interface UseDraggableEventArgs {
  event: CalendarSchedule;
  enabled: boolean;
  // Side effect to run when dragstart fires. Used by EventBlock to close its
  // hover Popover so the portaled panel can't intercept dragover/drop.
  onStart?: () => void;
}

/**
 * Drag-source side of calendar drag-and-drop. Spread `dragProps` onto the
 * element the user grabs. `wasDragging()` lets click handlers ignore the
 * synthetic click that fires immediately after dragend.
 *
 * On dragstart we record state and measure the vertical grab offset (so
 * DayDropLayer can snap the block's top, not the cursor, to a 30-min line),
 * then defer the source fade + drop-layer activation to the next animation
 * frame — applying
 * them synchronously aborts the drag in Chrome (it reads the style change as
 * the source being invalidated).
 */
export const useDraggableEvent = ({
  event,
  enabled,
  onStart,
}: UseDraggableEventArgs) => {
  const { draggedEventRef, wasDraggingRef, grabOffsetYRef, resetDragVisuals } = useDragContext();

  const onDragStart = (e: DragEvent<HTMLDivElement>) => {
    onStart?.();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", event.schedule_id);
    draggedEventRef.current = event;
    wasDraggingRef.current = true;

    const rect = e.currentTarget.getBoundingClientRect();
    grabOffsetYRef.current = e.clientY - rect.top;

    const source = e.currentTarget;
    requestAnimationFrame(() => {
      document.body.classList.add("schedule-drag-active");
      source.classList.add("schedule-dragging");
    });
  };

  const onDragEnd = (e: DragEvent<HTMLDivElement>) => {
    resetDragVisuals();
    e.currentTarget.classList.remove("schedule-dragging");
    // Defer one tick so the click that fires after dragend is still treated
    // as part of the drag and gets suppressed.
    setTimeout(() => {
      wasDraggingRef.current = false;
    }, 0);
  };

  const dragProps = enabled
    ? { draggable: true as const, onDragStart, onDragEnd }
    : { draggable: false as const };

  return {
    dragProps,
    wasDragging: () => wasDraggingRef.current,
  };
};
